import React, {createContext, ReactNode, useContext, useEffect, useRef, useState} from 'react';
import {HttpService} from "./http/Http.service";

type ProgressType = {
    processed: number;
    failed: number;
    total: number;
    progressPercent: number;
    errorMessage?: string;
} | null;

type ProgressContextType = {
    progress: ProgressType;
    isPolling: boolean;
    hideProgressBar?: boolean;
    startPolling: (uuid: string, callback?: Function, onError?: (data: ProgressType) => void, hideProgressBar?: boolean) => void;
    onError?: () => void;
    stopPolling: () => void;
};

const ProgressContext = createContext<ProgressContextType>({
    progress: null,
    isPolling: false,
    hideProgressBar: false,
    startPolling: () => {},
    stopPolling: () => {}
});

export const useProgressContext = () => useContext(ProgressContext);

type ProviderProps = {
    children: ReactNode;
};

export const ProgressProvider = ({ children }: ProviderProps) => {
    const [progress, setProgress] = useState<ProgressType>(null);
    const [isPolling, setIsPolling] = useState<boolean>(false);
    const [hideProgressBar, setHideProgressBar] = useState<boolean>(false);
    const intervalRef = useRef<number | null>(null);
    const JOB_KEY = 'progress-job-key'

    useEffect(() => {
        let uuid = localStorage.getItem(JOB_KEY);
        if(uuid) {
            startPolling(uuid);
        }
    }, []);
    const startPolling = (uuid: string, callback?: Function, onError?: (data: ProgressType) => void, hideProgressBar?: boolean) => {
        if (isPolling) {
            // un altro job è già in corso: segnaliamo il rifiuto, altrimenti il chiamante
            // resta in attesa di una callback che non arriverà mai
            if (onError) onError(null);
            return;
        }

        setIsPolling(true);

        setHideProgressBar(hideProgressBar ? hideProgressBar : false);
        const poll = async () => {
            try {
                const response = await HttpService.getInstanceAxios().get(`/progress/${uuid}`);
                const data = response.data;
                setProgress(data);

                if (data.progressPercent === 100) {
                    stopPolling();

                    if(data.failed > 0 && onError) {
                        onError(data);
                    } else if( callback )
                        callback();
                }
            } catch (error) {
                console.error('Polling error:', error);
                if(onError)
                    onError(null);
                stopPolling();
            }
        };
        localStorage.setItem(JOB_KEY,uuid);
        poll();
        intervalRef.current = window.setInterval(poll, 2500);
    };

    const stopPolling = () => {
        if (intervalRef.current !== null) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        localStorage.removeItem(JOB_KEY);
        setIsPolling(false);
    };

    useEffect(() => {
        return () => stopPolling();
    }, []);

    return (
        <ProgressContext.Provider value={{ progress, isPolling, hideProgressBar, startPolling, stopPolling }}>
            {children}
        </ProgressContext.Provider>
    );
};
